/**
 * Visualizer Factory for switching between Canvas and WebGL implementations
 * based on user preferences
 * 
 * Usage:
 * const oscilloscopeXY = createDualVisualizer(
 *   'oscilloscope-xy',
 *   OscilloscopeXYVisualizer,
 *   OscilloscopeXYWebGLVisualizer
 * );
 */

import { BaseVisualizer, VisualizerConfig, VisualizerMetadata, IVisualizer } from './types';

export function createDualVisualizer(
  visualizerId: string,
  CanvasVisualizer: new () => IVisualizer,
  WebGLVisualizer: new () => IVisualizer
): IVisualizer {
  // Create instances lazily only when needed
  let canvasImpl: IVisualizer | null = null;
  let webglImpl: IVisualizer | null = null;
  
  const getCanvasImpl = () => {
    if (!canvasImpl) canvasImpl = new CanvasVisualizer();
    return canvasImpl;
  };
  
  const getWebGLImpl = () => {
    if (!webglImpl) webglImpl = new WebGLVisualizer();
    return webglImpl;
  };

  class DualVisualizer extends BaseVisualizer {
    private _baseMetadata: VisualizerMetadata = {
      ...getCanvasImpl().metadata,
      id: visualizerId as any,
      description: `${getCanvasImpl().metadata.description} (Auto-selects Canvas/WebGL)`
    };

    // Return metadata based on current implementation
    get metadata(): VisualizerMetadata {
      const impl = this.getCurrentImpl();
      return {
        ...this._baseMetadata,
        usesWebGL: impl.metadata.usesWebGL || false
      };
    }

    private currentImpl: IVisualizer | null = null;

    private getCurrentImpl(): IVisualizer {
      // Check current preference
      let enableWebGL = true; // Default to WebGL enabled
      try {
        const stored = localStorage.getItem('resynthLab_userPreferences');
        if (stored) {
          const prefs = JSON.parse(stored);
          enableWebGL = prefs.enableWebGL !== false;
        }
      } catch (e) {
        // Fallback to WebGL default
      }

      // Return the correct implementation
      return enableWebGL ? getWebGLImpl() : getCanvasImpl();
    }

    onActivate(): void {
      this.currentImpl = this.getCurrentImpl();
      if (this.currentImpl?.onActivate) {
        this.currentImpl.onActivate();
      }
    }

    onDeactivate(): void {
      // Deactivate the current implementation
      if (this.currentImpl?.onDeactivate) {
        this.currentImpl.onDeactivate();
      }
      // Also deactivate the other one in case it was ever active
      if (canvasImpl && canvasImpl !== this.currentImpl && canvasImpl.onDeactivate) {
        canvasImpl.onDeactivate();
      }
      if (webglImpl && webglImpl !== this.currentImpl && webglImpl.onDeactivate) {
        webglImpl.onDeactivate();
      }
      this.currentImpl = null;
    }

    render(config: VisualizerConfig): number | void {
      const impl = this.getCurrentImpl();
      
      // If implementation changed, deactivate old and activate new
      if (this.currentImpl !== impl) {
        if (this.currentImpl?.onDeactivate) {
          this.currentImpl.onDeactivate();
        }
        this.currentImpl = impl;
        if (this.currentImpl?.onActivate) {
          this.currentImpl.onActivate();
        }
      }

      // Delegate to current implementation
      return this.currentImpl.render(config);
    }

    handleWheel?(event: WheelEvent, config: VisualizerConfig): boolean {
      const impl = this.getCurrentImpl();
      return impl.handleWheel?.(event, config) ?? false;
    }
  }

  return new DualVisualizer();
}
